import { calculaValoresFrete } from '@sotran/lib-shipper';
import { Col, DatePicker, notification, Row } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useHistory } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce/lib';

import Button from '../../../../../../components/Button';
import { ButtonCancel } from '../../../../../../components/Button/styles';
import { getFaixaTarifaTmov } from '../../../../../../helpers/calculaValoresLote';
import { formatarData } from '../../../../../../helpers/data';
import { novaMoneyConfig } from '../../../../../../helpers/form';
import {
  formatarMoeda,
  removeMoedaMask,
  toFixedNumber,
} from '../../../../../../helpers/formatador';
import apiShipper from '../../../../../../services/apiShipper';
import { useAgendamentoAlteracaoContext } from '../../../../Contextos/AgendamentoAlteracaoContexto';
import StatusAlteracaoEnum from '../../../../Enum/StatusAlteracaoEnum';
import TipoAlteracaoEnum from '../../../../Enum/TipoAlteracaoEnum';
import {
  AgendamentoContainer,
  AgendamentoInfo,
  AvisoValidar,
  CustomModal,
  FieldContainer,
} from '../styles';

const AlterarFreteModal = ({
  visible,
  setVisible,
  agendamento,
  setAgendamento,
  actionPayload,
  valorPedagio,
  tarifaTmovClienteLista,
}) => {
  const [confirmLoading, setConfirmLoading] = useState({
    agora: false,
    agendar: false,
    cancelar: false,
  });
  const [freteEmpresa, setFreteEmpresa] = useState(0);
  const [freteMotorista, setFreteMotorista] = useState(0);
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [showMensagemValidar, setShowMensagemValidar] = useState(false);
  const { removeAgendamentoAlteracao } = useAgendamentoAlteracaoContext();
  const history = useHistory();

  const resetLoading = () => {
    setConfirmLoading({
      agora: false,
      agendar: false,
      cancelar: false,
    });
  };

  const handleAlterarAgora = async () => {
    setConfirmLoading({ agora: true, agendar: false, cancelar: false });

    try {
      const payload = {
        data_alteracao: moment().format(),
        frete_empresa: freteEmpresa,
        frete_motorista: Number(freteMotorista),
        ...actionPayload,
      };

      await apiShipper.post('alteracao-demanda', payload);

      notification.success({
        message: 'Sucesso!',
        description: 'Frete empresa alterado com sucesso.',
      });
      history.push({
        pathname: `/detalhesDemanda/${actionPayload.id_demanda}`,
      });
    } catch {
      notification.error({
        message: 'Ocorreu um erro inesperado ao alterar o frete empresa',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const handleAgendar = async () => {
    setConfirmLoading({ agora: false, agendar: true, cancelar: false });

    try {
      const payload = {
        data_alteracao: moment(dataAgendamento)
          .utc(true)
          .format(),
        frete_empresa: freteEmpresa,
        frete_motorista: Number(freteMotorista),
        tipo_alteracao: TipoAlteracaoEnum.FRETE_EMPRESA,
        ...actionPayload,
      };

      await apiShipper.post('alteracao-frete-demanda', payload);

      notification.success({
        message: 'Sucesso!',
        description: 'Agendamento de alteração de frete realizado com sucesso.',
      });

      setAgendamento({ data: dataAgendamento, freteEmpresa });
      history.push({
        pathname: `/detalhesDemanda/${actionPayload.id_demanda}`,
      });
    } catch {
      notification.error({
        message:
          'Ocorreu um erro inesperado ao agendar alteração do frete da empresa',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const handleCancelAgendamento = async () => {
    setConfirmLoading({ agora: false, agendar: false, cancelar: true });
    try {
      await apiShipper.put('alteracao-demanda', {
        id_demanda: actionPayload.id_demanda,
        status: StatusAlteracaoEnum.status.CANCELADO,
        tipo_alteracao: TipoAlteracaoEnum.FRETE_EMPRESA,
      });

      notification.success({
        message: 'Agendamento de alteracao de frete cancelado com sucesso',
      });

      removeAgendamentoAlteracao(agendamento);
      setAgendamento({ data: null, freteEmpresa: null });
      setDataAgendamento(null);
    } catch (error) {
      notification.error({
        message: 'Ocorreu um erro inesperado ao cancelar o agendamento',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const calculaFretes = value => {
    setShowMensagemValidar(false);
    const tipoCalculo = 'freteEmpresa';
    const novoPedagio = toFixedNumber(valorPedagio, 2);
    const segmentacao = 'PJ';
    const isTarifaPercentual =
      tarifaTmovClienteLista.tipoTarifa === 'PERCENTUAL';

    const valor = removeMoedaMask(value || 0);

    setFreteEmpresa(valor);

    const tarifaTmovFaixa = getFaixaTarifaTmov(
      segmentacao,
      tarifaTmovClienteLista,
      valor,
      isTarifaPercentual
    );

    const resultado = calculaValoresFrete(
      valor,
      tipoCalculo,
      tarifaTmovFaixa,
      isTarifaPercentual,
      0,
      novoPedagio
    );

    const valorFormatado = toFixedNumber(resultado, 2);

    if (valorFormatado >= 1) {
      setFreteMotorista(valorFormatado);
    } else {
      setFreteMotorista(0);
      setShowMensagemValidar(true);
    }
  };

  const calculaFretesDebounced = useDebouncedCallback(calculaFretes, 300);

  useEffect(() => {
    if (!visible) {
      setDataAgendamento(null);
      setFreteEmpresa(null);
      setShowMensagemValidar(false);
    }
  }, [visible]);

  const mensagemAvisoValidar = () => 'Informe um valor de frete válido!';

  return (
    <CustomModal
      closable={false}
      title="Alterar frete empresa de todos os lotes"
      visible={visible}
      footer={[
        <Button key="fechar" type="secondary" onClick={() => setVisible()}>
          Fechar
        </Button>,
        <Button
          key="alterar"
          type="secondary"
          loading={confirmLoading.agora}
          onClick={handleAlterarAgora}
          disabled={!freteEmpresa || !freteMotorista || dataAgendamento}
        >
          Alterar agora
        </Button>,
        <Button
          key="agendar"
          type="primary"
          loading={confirmLoading.agendar}
          onClick={handleAgendar}
          disabled={!dataAgendamento || !freteEmpresa || !freteMotorista}
        >
          Agendar
        </Button>,
      ]}
    >
      <FieldContainer>
        <p>
          O novo valor será aplicado para todos os lotes. O frete motorista será
          calculado automaticamente.
        </p>
      </FieldContainer>

      <Row gutter={12}>
        <Col span={24}>
          <FieldContainer>
            <p>Frete empresa</p>
            <NumberFormat
              {...novaMoneyConfig}
              data-testid="frete-empresa"
              style={{ width: '40%' }}
              value={freteEmpresa || ''}
              onChange={e => calculaFretesDebounced(e.target.value)}
            />
          </FieldContainer>
          {showMensagemValidar && (
            <AvisoValidar>{mensagemAvisoValidar()}</AvisoValidar>
          )}
        </Col>
      </Row>

      <FieldContainer>
        <p>Agendar alteração</p>
        <AgendamentoContainer>
          <DatePicker
            value={dataAgendamento}
            data-testid="data-agendamento"
            aria-label="Data de agendamento"
            name="dataAgendamento"
            format="DD/MM/YYYY"
            disabled={agendamento.data}
            disabledDate={current => current.isBefore(moment())}
            style={{ width: '60%' }}
            onChange={data => setDataAgendamento(data)}
          />
          <div style={{ width: '10px' }} />
          {agendamento.data && (
            <ButtonCancel
              loading={confirmLoading.cancelar}
              onClick={handleCancelAgendamento}
            >
              Cancelar
            </ButtonCancel>
          )}
        </AgendamentoContainer>
      </FieldContainer>
      {agendamento.data && (
        <AgendamentoInfo>
          <p>{`Alteração agendada para ${formatarData(agendamento.data)}`}</p>
          <p>
            <b>{`Frete empresa: ${formatarMoeda(agendamento.freteEmpresa)}`}</b>
          </p>
        </AgendamentoInfo>
      )}
    </CustomModal>
  );
};

AlterarFreteModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  agendamento: PropTypes.shape({
    data: PropTypes.objectOf(PropTypes.any),
    freteEmpresa: PropTypes.number,
  }),
  setAgendamento: PropTypes.func.isRequired,
  actionPayload: PropTypes.shape({
    id_demanda: PropTypes.number,
    cliente_razao_social: PropTypes.string,
    origem: PropTypes.string,
    destino: PropTypes.string,
    email_filiais: PropTypes.arrayOf(PropTypes.string),
  }),
  valorPedagio: PropTypes.number.isRequired,
  tarifaTmovClienteLista: PropTypes.shape({
    tarifas: PropTypes.arrayOf(PropTypes.any),
    tipoTarifa: PropTypes.string,
  }),
};

AlterarFreteModal.defaultProps = {
  agendamento: null,
  actionPayload: null,
  tarifaTmovClienteLista: null,
};

export default AlterarFreteModal;
